import React, { useEffect, useState, useMemo, useRef } from "react";
import { Platform } from "react-native";
import { COLORS, STRINGS } from "../../constants";
import ErrorMessage from "../../sub/components/ErrorMessage";
import { isMobileWeb, handleLinkingPress } from "../../utils/helper";
import AppSettings from "../../storage/appSettings";

// Constants
const BANNER_BOTTOM = 70;
const BANNER_RIGHT_MOBILE = 16;
const BANNER_RIGHT_DESKTOP = 80;
const BANNER_Z_INDEX = 9999;

/**
 * Detects user platform from navigator.userAgent.
 * iOS check takes priority over Android check.
 * @returns {"ios" | "android" | "desktop"}
 */
const detectPlatform = () => {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

/**
 * InstallPromptWeb - Displays a floating install/open banner on web platforms.
 *
 * - iOS: Shows "Install App" button → opens Apple App Store
 * - Android: Shows "Open App" (deep link) + "Install" (Play Store) buttons
 * - Desktop: Shows "Add to Home Screen" → triggers PWA install or shows fallback instructions
 *
 * @component
 * @returns {JSX.Element|null} The install prompt banner, or null if hidden/non-web.
 */
const InstallPromptWeb = () => {
  const isWeb = Platform.OS === "web";
  const setWebInstaller = AppSettings((state) => state.setWebInstaller);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const mountedRef = useRef(true);

  // Memoize platform detection — navigator.userAgent never changes mid-session
  const isMobile = useMemo(() => (isWeb ? isMobileWeb() : false), [isWeb]);
  const userAgent = useMemo(() => (isWeb ? detectPlatform() : "desktop"), [isWeb]);

  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Show prompt on ALL web browsers (desktop and mobile)
    setShowPrompt(true);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [isWeb]);

  const handleClick = async () => {
    if (isMobile) {
      if (userAgent === "ios") {
        handleLinkingPress(STRINGS.appleAppstoreLink);
      } else {
        setErrorMessage("Your device is not supported for direct installation.");
        setShowError(true);
      }
    } else {
      if (deferredPrompt && !isInstalling) {
        setIsInstalling(true);
        try {
          await deferredPrompt.prompt();
          const { outcome } = await deferredPrompt.userChoice;

          if (!mountedRef.current) {
            return;
          }

          if (outcome === "accepted") {
            setWebInstaller({ installed: true });
          }
        } catch (error) {
          console.error("Install prompt error:", error);
          if (mountedRef.current) {
            setErrorMessage("Unable to install. Please try again.");
            setShowError(true);
          }
        } finally {
          if (mountedRef.current) {
            setIsInstalling(false);
            setDeferredPrompt(null);
          }
        }
      } else if (!deferredPrompt) {
        setErrorMessage("To install, tap the browser menu and select 'Add to Home screen'");
        setShowError(true);
      }
    }
  };

  const handleOpenApp = () => {
    const cleanup = () => {
      clearTimeout(fallbackTimeout);
      document.removeEventListener("visibilitychange", handleVisibility);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        cleanup();
      }
    };

    // Fallback to Play Store if the deep link doesn't open the app within 1.5s
    const fallbackTimeout = setTimeout(() => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (mountedRef.current) {
        handleLinkingPress(STRINGS.googlePlaystoreLink);
      }
    }, 1500);

    document.addEventListener("visibilitychange", handleVisibility);

    window.location = "vbgo://";
  };

  const handleInstallApp = () => {
    handleLinkingPress(STRINGS.googlePlaystoreLink);
  };

  if (!isWeb || !showPrompt) {
    return null;
  }

  const isAndroid = userAgent === "android";
  const label = isMobile ? "Install App" : "Add to Home Screen";

  return (
    <>
      {showError && (
        <div style={{ position: "fixed", inset: 0, zIndex: BANNER_Z_INDEX + 1 }}>
          <ErrorMessage
            message={errorMessage}
            showError={showError}
            onClose={() => setShowError(false)}
          />
        </div>
      )}
      <div style={styles.banner} role="complementary">
        <img src="/assets/icons/icon-96.webp" alt="VBGo" style={styles.icon} />
        <div style={styles.text}>
          <div style={styles.title}>VBGo</div>
        </div>
        {isAndroid ? (
          <>
            <button
              style={styles.button}
              onClick={handleOpenApp}
              aria-label="Open VBGo app"
            >
              Open App
            </button>
            <button
              style={styles.button}
              onClick={handleInstallApp}
              aria-label="Install VBGo from Play Store"
            >
              Install
            </button>
          </>
        ) : (
          <button
            style={styles.button}
            onClick={handleClick}
            disabled={isInstalling}
            aria-label={label}
          >
            {isInstalling ? "Installing..." : label}
          </button>
        )}
        <button
          style={styles.close}
          onClick={() => {
            setShowPrompt(false);
          }}
          aria-label="Close install prompt"
        >
          ×
        </button>
      </div>
    </>
  );
};

const getStyles = (isMobile) => ({
  banner: {
    position: "fixed",
    bottom: BANNER_BOTTOM,
    right: isMobile ? BANNER_RIGHT_MOBILE : BANNER_RIGHT_DESKTOP,
    backgroundColor: COLORS.backgroundGreen,
    border: `1px solid ${COLORS.primary}`,
    borderRadius: 12,
    padding: isMobile ? "12px 16px" : "14px 18px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    zIndex: BANNER_Z_INDEX,
    maxWidth: isMobile ? "90%" : 460,
  },
  icon: {
    width: isMobile ? 48 : 40,
    height: isMobile ? 48 : 40,
    borderRadius: 8,
    marginRight: 12,
  },
  text: {
    flex: 1,
    fontSize: isMobile ? 14 : 13,
    lineHeight: "1.2em",
  },
  title: {
    fontWeight: "bold",
    color: COLORS.textDark,
  },
  button: {
    backgroundColor: COLORS.primary,
    color: "white",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: "bold",
    marginLeft: 8,
    cursor: "pointer",
  },
  close: {
    background: "none",
    border: "none",
    fontSize: 18,
    marginLeft: 10,
    cursor: "pointer",
    color: COLORS.border,
  },
});

export default InstallPromptWeb;
